import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { first } from 'lodash';
import { bindActionCreators } from 'redux';

import addStylist from 'actions/stylist/addStylist.action';
import deleteStylist from 'actions/stylist/deleteStylist.action';
import editStylist from 'actions/stylist/editStylist.action';
import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import fetchUserEmailsBySalon from 'actions/user/fetchUserEmailsBySalon.action';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import { NO } from 'constants/no';
import TRANSFER_POLICY_TEXT_LOWER from 'constants/pos/stripeTransferPolicyTextLower.const';
import { withTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import getAny from 'utils/accessors/getAny.util';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { fromPropsToMatchedSalonId } from 'utils/converters/props/bl.util';
import { bodAsIso } from 'utils/date/now.util';
import logger from 'utils/logger.util';
import byId from 'utils/predicates/byId.util';
import isFalsy from 'utils/predicates/isFalsy.util';
import isTruthy from 'utils/predicates/isTruthy.util';

import PopupModalContainer from 'common/PopupModal';
import { getRoles } from 'onboarding/roles/actions';
import { getCategories, getFutureAppointmentsWithService } from 'onboarding/services/actions';
import {
  clearRequestError,
  removeSavedStylistsIds,
  resetStylistSaved,
  setStylistSaved,
  updateRolePrice,
} from 'onboarding/stylists/actions';
import StylistsScreen from 'onboarding/stylists/StylistsScreen';

import CN from './StylistsLayout.module.scss';

const L = logger('StylistsLayout');

const SNIPPET_TWO_STYLISTS = 'Two stylists in the salon';
const SNIPPET_MANDATORY_PERMISSION = 'permission level is manadatory';
const SNIPPET_DISABLE_EMAIL_LOGIN = 'The email has login to access more than one salon';

export class StylistsLayout extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      errorMessage: '',
      savingInProgress: false,
      editedStylistsIds: [],
      settingsSavedStylistsTimerIds: [],
      addStylistExpanded: false, // props.stylistIsSaved,
      loaded: {
        stylists: false,
        categories: false,
        roles: false,
      },
      changedStylist: null,
      popupTitle: '',
      popupMessage: '',
      showSelfEmployedErrorModal: false,
    };
  }

  UNSAFE_componentWillMount() {
    const { fetchStylistsBySalon, getCategories, resetStylistSaved, getRoles } = this.props;

    const salonId = fromPropsToMatchedSalonId(this.props);

    L.debug('UNSAFE_componentWillMount()', salonId, this.props);

    L.unignore('UNSAFE_componentWillMount()', 'fetchStylistsBySalon:', fetchStylistsBySalon(salonId));
    L.unignore('UNSAFE_componentWillMount()', 'getCategories:', getCategories(salonId));
    L.unignore('UNSAFE_componentWillMount()', 'getRoles:', getRoles(salonId));
    L.unignore('UNSAFE_componentWillMount()', 'resetStylistSaved:', resetStylistSaved());
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      isPosting: oldIsPosting,
      stylistIsSaved: oldStylistIsSaved,
      editStylist,
      removeSavedStylistsIds,
      setStylistSaved,
      resetStylistSaved,
      fetching: oldFetching,
      futureAppointments: oldFutureAppointments,
    } = this.props;
    const {
      isPosting: newIsPosting,
      requestErrorMessage: newRequestErrorMessage,
      stylistIsSaved: newStylistIsSaved,
      settingsSavedStylistsIDs: newSettingsSavedStylistsIDs,
      fetching: newFetching,
      futureAppointments: newFutureAppointments,
    } = nextProps;

    if (isFalsy(newIsPosting) && isTruthy(oldIsPosting)) {
      // isPosting was true, now is false, this means saving to BE is finished
      L.debug('componentwillReceiveProps()', 'POSTED');
    }

    if (!oldStylistIsSaved && newStylistIsSaved) {
      if (this.state.addStylistExpanded && this.state.savingInProgress) {
        this.setState({ savingInProgress: false });
        setStylistSaved();
        setTimeout(() => resetStylistSaved(), 2000);
      }
    }

    // Handle the stylists that have been marked as saved with the last request
    const differenceStylists = newSettingsSavedStylistsIDs.filter(({ isNew }) => isNew).map(({ id }) => id);
    const settingsSavedStylistsTimerIds = this.state.settingsSavedStylistsTimerIds.slice(0);

    if (differenceStylists.length) {
      const timerId = window.setTimeout(() => {
        removeSavedStylistsIds(differenceStylists);
        differenceStylists.forEach((id) => settingsSavedStylistsTimerIds.splice(id, 1));
      }, 2000);

      differenceStylists.forEach((id) => {
        window.clearTimeout(settingsSavedStylistsTimerIds[id]);
        settingsSavedStylistsTimerIds[id] = timerId;
      });
    }

    // Check if the loading screen should be removed by checking if all the needed things are loaded
    if (isFalsy(oldFetching.categories) && isTruthy(newFetching.categories)) {
      const loaded = Object.assign(this.state.loaded);
      loaded.categories = true;
      this.setState({ loaded });
    }

    if (isTruthy(oldFetching.roles) && isFalsy(newFetching.roles)) {
      const loaded = Object.assign(this.state.loaded);
      loaded.roles = true;
      this.setState({ loaded });
    }

    if (isTruthy(oldFetching.stylists) && isFalsy(newFetching.stylists)) {
      const loaded = Object.assign(this.state.loaded);
      loaded.stylists = true;
      this.setState({ loaded });
    }

    if (
      newFutureAppointments &&
      newFutureAppointments.response &&
      this.state.changedStylist &&
      newFutureAppointments.response !== oldFutureAppointments.response
    ) {
      editStylist({ stylist: this.state.changedStylist, salonId: fromPropsToMatchedSalonId(this.props) });
    }

    this.setState({ settingsSavedStylistsTimerIds });

    if (newRequestErrorMessage.includes(SNIPPET_TWO_STYLISTS)) {
      this.setState({ errorMessage: this.props.t('staffAndBusiness.stylistProfiles.errorMessages.duplicateEmail') });
    } else if (newRequestErrorMessage.includes(SNIPPET_MANDATORY_PERMISSION)) {
      this.setState({ errorMessage: this.props.t('staffAndBusiness.stylistProfiles.errorMessages.mandPermission') });
    } else if (newRequestErrorMessage.includes(SNIPPET_DISABLE_EMAIL_LOGIN)) {
      this.setState({ errorMessage: this.props.t('staffAndBusiness.stylistProfiles.errorMessages.emailLogin') });
    } else {
      this.setState({ errorMessage: newRequestErrorMessage /* BE user_id error */ });
    }
  }

  handleEditing = (options) => {
    L.debug('handleEditing()', options);
    const { stylistId, stylist: changedStylist } = options;
    const { editedStylistsIds } = this.state;
    const salonId = fromPropsToMatchedSalonId(this.props);
    const {
      stylists: { stylists },
      getFutureAppointmentsWithService,
    } = this.props;

    if (editedStylistsIds.indexOf(stylistId) === NO.found) {
      this.setState({ editedStylistsIds: [...editedStylistsIds, stylistId] });
    } else {
      const firstStylist = first(stylists.filter(byId(stylistId)));

      if (changedStylist && changedStylist.role.services.length === 0) {
        this.setState({
          popupMessage: this.props.t('staffAndBusiness.stylistProfiles.errorMessages.popupMessage'),
          popupTitle: this.props.t('staffAndBusiness.stylistProfiles.errorMessages.popupTitle'),
        });
      }

      const roleServices = firstStylist.role && firstStylist.role.services;
      if (roleServices && roleServices.length > 0) {
        const today = bodAsIso();
        const { services } = firstStylist.role;
        const servicesRoleIds = services ? services.map(({ id }) => id) : [];
        if (servicesRoleIds && servicesRoleIds.length > 0) {
          getFutureAppointmentsWithService(salonId, 0, servicesRoleIds, today);
          this.setState({ changedStylist, editedStylistsIds: [] });
        }
      } else {
        this.setState({ changedStylist });
      }
    }

    this.setState({ addStylistExpanded: true });
  };

  hideError = () => {
    this.setState({ errorMessage: '', popupMessage: '' });
    this.props.clearRequestError();
  };

  handleSaveAddStylist = (options) => {
    L.debug('handleSaveAddStylist()', options, this.props, this.state);
    this.setState({ savingInProgress: true });
    const promise = this.props.addStylist(options);
    return this.refreshData({ promise });
  };

  handleSaveEditStylist = (options) => {
    L.debug('handleSaveEditStylist()', options, this.props, this.state);

    const stylistId = getAny(null, 'stylist.id', options);
    const stylistsArray = this.props.stylists?.stylists || [];
    const stylist = asArray(stylistsArray).find((s) => s.id === stylistId);
    const currentTransferPolicy = stylist?.transfer_policy;
    const currentSelfEmployed = fromAnyToBool(stylist?.is_self_employed);

    const newValueSelfEmployed = fromAnyToBool(getAny(null, 'stylist.is_self_employed', options));
    const isChangingToEmployed = currentSelfEmployed && !newValueSelfEmployed;

    const isSelfEmployedTransferPolicy =
      currentTransferPolicy === TRANSFER_POLICY_TEXT_LOWER.allTransfers ||
      currentTransferPolicy === TRANSFER_POLICY_TEXT_LOWER.splitCommissions;

    if (isChangingToEmployed && isSelfEmployedTransferPolicy) {
      this.setState({ showSelfEmployedErrorModal: true });
      return;
    }

    const promise = this.props.editStylist({ ...options, stylistId });
    return this.refreshData({ promise });
  };

  refreshData = ({ promise }) => {
    promise
      .then((r) => void L.debug('refreshData()', 'then()', r))
      .catch((e) => void L.warn('refreshData()', 'catch()', e))
      .finally(() => {
        const salonId = fromPropsToMatchedSalonId(this.props);
        L.debug('refreshData()', 'finally()', salonId);
        this.props.fetchStylistsBySalon(salonId);
        this.props.fetchUserEmailsBySalon(salonId);
      });

    return promise;
  };

  render() {
    const { isDarkMode, t } = this.props;
    const {
      errorMessage,
      loaded,
      savingInProgress,
      editedStylistsIds,
      addStylistExpanded,
      showSelfEmployedErrorModal,
    } = this.state;

    const popupTitle = this.state.popupTitle || t('common.error');
    const popupMessage = this.state.popupMessage || errorMessage;

    const salonId = fromPropsToMatchedSalonId(this.props);

    return (
      <div
        data-bem="StylistsLayout"
        className={cn({
          'services online-booking-layout online-booking': true,
          [CN.component]: true,
          [CN.darkMode]: isDarkMode,
        })}
      >
        {(!loaded.categories || !loaded.stylists || !loaded.roles) && <OverlaySpinner />}
        {(errorMessage || popupMessage) && (
          <PopupModalContainer
            popupTitle={popupTitle}
            popupMessage={popupMessage}
            buttonText={t('common.buttons.okay')}
            buttonCallback={this.hideError}
          />
        )}

        <StylistsScreen
          salonId={salonId}
          editedStylistsIds={editedStylistsIds}
          hideAddBtn={addStylistExpanded}
          hideBtnWhenEditStylist={editedStylistsIds.length >= 1}
          editStylist={this.handleEditing}
          futureAppointments={this.props.futureAppointments}
          settingsSavedStylistsIDs={this.props.settingsSavedStylistsIDs.map((stylist) => stylist.id)}
          settingsErrorStylistsIDs={this.props.settingsErrorStylistsIDs}
          deleteStylist={(stylistId) => void this.props.deleteStylist({ stylistId, salonId })}
          onAddMoreClick={() => void this.setState({ addStylistExpanded: !addStylistExpanded, editedStylistsIds: [] })}
          addStylistBtn={this.handleSaveAddStylist}
          editStylistSaveBtn={this.handleSaveEditStylist}
          addBtnDisabled={savingInProgress}
          updateRolePrice={this.props.updateRolePrice}
          blockAlertSuccess={showSelfEmployedErrorModal}
        />

        <SalonModal
          tag="StylistsLayout__modal_changes_not_saved"
          isOpen={showSelfEmployedErrorModal}
          variant="noFooter"
        >
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <Button variant="empty" onClick={() => void this.setState({ showSelfEmployedErrorModal: false })}>
                <Icon variant="close" variantInvert />
              </Button>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <IconStatus variant="warning">
              <Icon variant="warningCircle" width="l1" />
            </IconStatus>
            <div className={CN.modalContent}>
              <div className={CN.modalTitle}>{t('staffAndBusiness.stylistProfiles.notSavedModal.title')}</div>
              <div className={CN.modalText}>{t('staffAndBusiness.stylistProfiles.notSavedModal.description')}</div>
            </div>
          </SalonModal.Body>
        </SalonModal>
      </div>
    );
  }
}

export const mapStateToProps = (state) => ({
  requestErrorMessage: state.onboarding.stylists.errorMessage,
  stylistIsSaved: state.onboarding.stylists.isSaved,
  settingsSavedStylistsIDs: state.onboarding.stylists.settingsSavedStylistsIDs,
  settingsErrorStylistsIDs: state.onboarding.stylists.settingsErrorStylistsIDs,
  isPosting: state.onboarding.stylists.isPosting,
  fetching: {
    stylists: state.onboarding.stylists.isFetching,
    categories: state.onboarding.services.areCategoriesFetched,
    roles: state.onboarding.roles.isFetching,
  },
  roles: state.onboarding.roles,
  stylists: state.onboarding.stylists,
  futureAppointments: state.onboarding.services.changePrice.futureAppointments,
});

export const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchUserEmailsBySalon,
      fetchStylistsBySalon,
      getCategories,
      getRoles,
      resetStylistSaved,
      setStylistSaved,
      clearRequestError,
      addStylist,
      editStylist,
      deleteStylist,
      removeSavedStylistsIds,
      updateRolePrice,
      getFutureAppointmentsWithService,
    },
    dispatch,
  );

const StylistsLayoutHoc = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(withTheme()(StylistsLayout)));

export default StylistsLayoutHoc;
